import { getAnalytics } from './analytics';
import {
  AnalyticsEventProperties,
  AnalyticsTarget,
  UnionOfEventsWithProperties,
} from './types';
import { useCallback } from 'react';
type Nullable<T> = { [key in keyof T]: T[keyof T] | null };

type UTMParams = Nullable<{
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;
  utm_id: string;
}>;

const isBrowser = globalThis.sessionStorage !== undefined;

const getUTMParams = (): UTMParams => {
  if (!isBrowser) return { utm_source: null, utm_medium: null, utm_campaign: null, utm_term: null, utm_content: null, utm_id: null };
  const urlParams = new URLSearchParams(globalThis.location.search);
  return {
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    utm_term: urlParams.get('utm_term'),
    utm_content: urlParams.get('utm_content'),
    utm_id: urlParams.get('utm_id'),
  };
};

export const initializeAnalytics = () => {
  if (!isBrowser) return;
  if (!sessionStorage.getItem('initial_referrer')) {
    sessionStorage.setItem('initial_referrer', document.referrer);
    // sessionStorage.setItem('initial_landing_page', window.location.href);
    sessionStorage.setItem(
      'initial_utm_params',
      JSON.stringify(getUTMParams()),
    );
  }
};

export const getCommonProperties = () => {
  if (!isBrowser) return {};
  return {
    initial_referrer:
      sessionStorage.getItem('initial_referrer') ?? document.referrer,
  };
};

const getTrackingProperties = (additionalProps = {}, withCampaign = false) => {
  const utmParams = isBrowser
    ? JSON.parse(sessionStorage.getItem('initial_utm_params') || '{}')
    : {};
  const commonProps = getCommonProperties();
  const campaign = withCampaign
    ? {
        name: utmParams.utm_campaign,
        source: utmParams.utm_source,
        medium: utmParams.utm_medium,
        term: utmParams.utm_term,
        content: utmParams.utm_content,
        id: utmParams.utm_id,
      }
    : {};
  return {
    ...commonProps,
    ...additionalProps,
    ...(Object.keys(campaign).length ? campaign : undefined),
  };
};

export const useAnalytics = <TEvents extends string, TEventsWithProps>(
  additionalProperties?: Record<string, unknown>,
) => {
  // const analyticsContext = await getAnalytics();

  // const { track, identify, page } = analyticsContext;

  const trackPageView = useCallback(async () => {
    const { page } = await getAnalytics();
    // Properties go in the 3rd slot, where the wrapper merges super properties
    page(undefined, undefined, getTrackingProperties(additionalProperties));
  }, [additionalProperties]);

  const identifyUser = async (
    userId: string,
    userTraits?: Record<string, unknown>,
  ) => {
    const { identify } = await getAnalytics();

    identify(userId, {
      ...getTrackingProperties(additionalProperties),
      ...userTraits,
    });
  };
  // Type for the overloaded trackEvent function
  type TrackEvent = {
    <T extends AnalyticsTarget<TEvents, TEventsWithProps>>(
      event: UnionOfEventsWithProperties<T['EventsWithProps']>,
      properties: UnionOfEventsWithProperties<T['EventsWithProps']>,
    ): void;
    <
      T extends AnalyticsTarget<TEvents, TEventsWithProps>,
      EventName extends Exclude<
        T['Events'],
        keyof UnionOfEventsWithProperties<T['EventsWithProps']>
      >,
    >(
      event: EventName,
      properties?: AnalyticsEventProperties<
        TEvents,
        TEventsWithProps,
        T,
        EventName
      >,
    ): void;
  };

  const trackEvent = useCallback(
    async <
      T extends AnalyticsTarget<TEvents, TEventsWithProps>,
      EventName extends T['Events'],
    >(
      event: EventName,
      properties: AnalyticsEventProperties<
        TEvents,
        TEventsWithProps,
        T,
        EventName
      > = {} as AnalyticsEventProperties<
        TEvents,
        TEventsWithProps,
        T,
        EventName
      >,
    ) => {
      const { track } = await getAnalytics();
      const props =
        properties && typeof properties === 'object' ? properties : {};

      track(event, {
        ...getTrackingProperties(additionalProperties),
        ...props,
      });
    },
    [additionalProperties],
  ) as TrackEvent;

  return {
    trackEvent,
    trackPageView,
    identifyUser,
  };
};
